/**
 * Canonical metric ID map — deterministic UUID per metric type for
 * idempotent backend upserts. These IDs are agreed-upon constants
 * shared with the backend.
 *
 * Do not modify without coordinating with the backend team.
 */
export const METRIC_ID_MAP = {
  Pulse: "a1b2c3d4-1001-4000-8000-000000000001",
  RestingHeartRate: "a1b2c3d4-1002-4000-8000-000000000002",
  Hrv: "a1b2c3d4-1003-4000-8000-000000000003",
  SystolicBloodPressure: "BF219A6E-AEFC-4599-966E-E99DF6CA6FE4",
  DiastolicBloodPressure: "95FD2E6B-FE20-4C08-90A5-82E0A5E5C617",
  SaturationOfPeripheralOxygen: "8CBAC00B-3A03-40CA-8B94-7BE1EC5925FE",
  BreathingRate: "58BBC423-74A5-4DE3-BE2E-D05C715CB967",
  Temperature: "E4BC22CE-9A99-4B7A-A23F-9A04AD16ECCB",
  Weight: "1ED603F6-C984-40D5-86AE-1F22E73DC88B",
  Height: "4F34B235-BFF8-44E9-8546-9EB8E9A1AF52",
  BodyMassIndex: "7B6C7EBC-4F29-4A5B-88A1-43C59E578C4B",
  BodyFat: "9863AF43-30F8-4096-A477-FECF1C562A79",
  FatFreeMass: "A9B51F1F-0E2C-4654-9A24-EB3E8721D1D4",
  Steps: "a1b2c3d4-1014-4000-8000-000000000014",
  Distance: "a1b2c3d4-1015-4000-8000-000000000015",
  ActiveEnergy: "a1b2c3d4-1016-4000-8000-000000000016",
  ExerciseTime: "a1b2c3d4-1017-4000-8000-000000000017",
  StandTime: "a1b2c3d4-1018-4000-8000-000000000018",
  SleepAnalysis: "a1b2c3d4-1020-4000-8000-000000000020",
  SleepDuration: "a1b2c3d4-1021-4000-8000-000000000021",
  DietaryEnergy: "a1b2c3d4-1022-4000-8000-000000000022",
  DietaryWater: "a1b2c3d4-1023-4000-8000-000000000023",
  MindfulMinutes: "a1b2c3d4-1024-4000-8000-000000000024",
} as const;

/** PascalCase canonical metric type (matches backend contract) */
export type HealthDataType = keyof typeof METRIC_ID_MAP;

/**
 * Maps snake_case types sent by the native health SDK to canonical
 * PascalCase metric types used by the backend.
 */
export const SDK_TO_METRIC_TYPE = {
  heart_rate: "Pulse",
  resting_heart_rate: "RestingHeartRate",
  hrv: "Hrv",
  blood_pressure_systolic: "SystolicBloodPressure",
  blood_pressure_diastolic: "DiastolicBloodPressure",
  blood_oxygen: "SaturationOfPeripheralOxygen",
  respiratory_rate: "BreathingRate",
  body_temperature: "Temperature",
  weight: "Weight",
  height: "Height",
  bmi: "BodyMassIndex",
  body_fat: "BodyFat",
  lean_body_mass: "FatFreeMass",
  steps: "Steps",
  distance: "Distance",
  active_energy: "ActiveEnergy",
  exercise_time: "ExerciseTime",
  stand_time: "StandTime",
  sleep_analysis: "SleepAnalysis",
  sleep_duration: "SleepDuration",
  dietary_energy: "DietaryEnergy",
  dietary_water: "DietaryWater",
  mindful_minutes: "MindfulMinutes",
} as const satisfies Record<string, HealthDataType>;

/** Snake_case type sent by the native health SDK over the bridge */
export type SDKDataType = keyof typeof SDK_TO_METRIC_TYPE;

export const ALL_SDK_DATA_TYPES = Object.keys(
  SDK_TO_METRIC_TYPE
) as SDKDataType[];

/** Phase 1 minimum — only the data types needed for Health Score */
export const REQUIRED_SDK_DATA_TYPES = [
  "exercise_time",
  "steps",
  "respiratory_rate",
  "height",
  "weight",
  "sleep_analysis",
  "sleep_duration",
  "blood_pressure_systolic",
  "blood_pressure_diastolic",
  "resting_heart_rate",
] as const satisfies readonly SDKDataType[];

/**
 * Resolves a snake_case SDK data type string to its canonical
 * PascalCase HealthDataType. Returns undefined for unknown types.
 */
export function resolveMetricType(sdkType: string): HealthDataType | undefined {
  return SDK_TO_METRIC_TYPE[sdkType as SDKDataType];
}
